/**
 * SocialLoginButtons — Google + Microsoft SSO triggers.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. Redirect URIs are configured server-side in env vars
 * and registered in Google Cloud / Azure portals.
 *
 * Flow:
 *   1. Component queries GET /api/auth/providers to know which buttons to show.
 *   2. On click, it asks the backend for the provider's authorize URL (which
 *      embeds the SERVER-CONFIGURED redirect_uri + state) and redirects the
 *      browser to it.
 *   3. After consent, the provider redirects to https://saarone.com/auth/google
 *      (or /auth/microsoft/callback) → handled by OAuthCallback.jsx.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public — providers status is unauthenticated
async function fetchProviders() {
  try { const r = await axios.get(`${API}/auth/providers`); return r.data; }
  catch { return { google_enabled: false, microsoft_enabled: false }; }
}

// Build the Google authorize URL on the client side using a PUBLIC config
// endpoint (so unauthenticated visitors can sign in). For OAuth security the
// SECRET stays server-side; we just need client_id + redirect_uri to build the
// URL. We fetch them via a tiny public config endpoint so they're not baked
// into the React bundle and can be changed without a redeploy.
async function buildGoogleAuthUrl(state) {
  const cfg = (await axios.get(`${API}/auth/sso/config`)).data;
  if (!cfg.google) throw new Error("Google not configured");
  const params = new URLSearchParams({
    client_id: cfg.google.client_id,
    redirect_uri: cfg.google.redirect_uri,
    response_type: "code",
    scope: "openid email profile",
    access_type: "online",
    prompt: "select_account",
    state,
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

async function buildMicrosoftAuthUrl(state) {
  const cfg = (await axios.get(`${API}/auth/sso/config`)).data;
  if (!cfg.microsoft) throw new Error("Microsoft not configured");
  const params = new URLSearchParams({
    client_id: cfg.microsoft.client_id,
    redirect_uri: cfg.microsoft.redirect_uri,
    response_type: "code",
    scope: "openid profile email User.Read",
    prompt: "select_account",
    response_mode: "query",
    state,
  });
  return `https://login.microsoftonline.com/${cfg.microsoft.tenant_id}/oauth2/v2.0/authorize?${params}`;
}

export default function SocialLoginButtons() {
  const [enabled, setEnabled] = useState({ google_enabled: false, microsoft_enabled: false });

  useEffect(() => { fetchProviders().then(setEnabled); }, []);

  if (!enabled.google_enabled && !enabled.microsoft_enabled) return null;

  const start = async (provider) => {
    const state = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    sessionStorage.setItem("sso_state", state);
    sessionStorage.setItem("sso_provider", provider);
    try {
      const url = provider === "google" ? await buildGoogleAuthUrl(state) : await buildMicrosoftAuthUrl(state);
      window.location.href = url;
    } catch (e) {
      alert(`${provider} sign-in is not available yet. Please use email + password.`);
    }
  };

  return (
    <div className="space-y-2.5 mb-5" data-testid="sso-buttons">
      <div className="flex items-center gap-3 text-xs text-zinc-500">
        <span className="flex-1 border-t border-zinc-200" />
        <span>Or continue with</span>
        <span className="flex-1 border-t border-zinc-200" />
      </div>
      <div className="grid grid-cols-1 gap-2">
        {enabled.google_enabled && (
          <Button
            type="button"
            variant="outline"
            onClick={() => start("google")}
            className="h-11 bg-white border-zinc-300 rounded-md hover:bg-zinc-50 text-zinc-900 font-medium"
            data-testid="sso-google-btn"
          >
            <GoogleIcon /> Continue with Google
          </Button>
        )}
        {enabled.microsoft_enabled && (
          <Button
            type="button"
            variant="outline"
            onClick={() => start("microsoft")}
            className="h-11 bg-white border-zinc-300 rounded-md hover:bg-zinc-50 text-zinc-900 font-medium"
            data-testid="sso-microsoft-btn"
          >
            <MicrosoftIcon /> Continue with Microsoft
          </Button>
        )}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20.4H24v7.2h11.3c-1.7 4.6-6 7.8-11.3 7.8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.6 6.2 29 4.4 24 4.4 13.2 4.4 4.4 13.2 4.4 24S13.2 43.6 24 43.6c11 0 19.8-8 19.8-19.6 0-1.2-.1-2.4-.2-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.1l5.9 4.3c1.6-3.7 5.2-6.4 9.8-6.4 3 0 5.8 1.1 7.9 3l5.1-5.1C33.6 6.2 29 4.4 24 4.4 16.5 4.4 9.9 8.6 6.3 14.1z"/>
      <path fill="#4CAF50" d="M24 43.6c5 0 9.5-1.8 13-4.8l-5.6-4.7c-2 1.4-4.6 2.3-7.4 2.3-5.2 0-9.6-3.3-11.2-7.9l-5.9 4.5C9.9 39.3 16.4 43.6 24 43.6z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20.4H24v7.2h11.3c-.8 2.3-2.3 4.2-4.3 5.5l5.6 4.7c-.4.3 6.6-4.8 6.6-13.9 0-1.2-.1-2.4-.2-3.4z"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
      <path fill="#f25022" d="M1 1h10v10H1z"/>
      <path fill="#00a4ef" d="M1 12h10v10H1z"/>
      <path fill="#7fba00" d="M12 1h10v10H12z"/>
      <path fill="#ffb900" d="M12 12h10v10H12z"/>
    </svg>
  );
}
